import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  ReactNode,
} from "react"
import { API } from "../config/api"

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface Permission {
  id: number
  name: string
  description: string | null
}

export interface Role {
  id: number
  name: string
  description: string | null
  permissions: Permission[]
}

export interface DealerGroupBrief {
  id: number
  name: string
  slug: string
  /** The group's website by name ("dolanautogroup.com"), null when none is
   * configured — known from login, so a page can name the site on its first
   * paint instead of after its data arrives. */
  website_domain: string | null
}

export interface RooftopBrief {
  id: number
  name: string
  slug: string
}

export interface User {
  id: number
  username: string
  email: string | null
  first_name: string | null
  last_name: string | null
  role: Role | null      // legacy primary role (first selected)
  roles: Role[]          // all roles held; permissions are the union (multi-role)
  dealer_group: DealerGroupBrief | null
  // Home store — the store the audit page opens on for this user. A default
  // view, not a security boundary: any user can switch stores on the page.
  rooftop: RooftopBrief | null
  is_active: boolean
  is_first_login: boolean
  last_login_at: string | null
}

interface AuthContextValue {
  user: User | null
  token: string | null
  isLoading: boolean
  login: (username: string, password: string) => Promise<User>
  logout: () => void
  // Keep a login INVI handed back (after a password change, which retires the old one).
  adoptToken: (newToken: string) => void
  hasPermission: (permission: string) => boolean
  refreshUser: () => Promise<void>
}

const TOKEN_KEY = "invi_token"

// Waits between retries of the page-load "who am I?" check. A check that never
// reaches INVI (the dealership network drops some requests) is retried rather
// than read as a logout: deleting the token on any failure logged Ian out twice
// in ten minutes on 2026-09-30, each time he refreshed a page.
const IDENTITY_CHECK_RETRY_DELAYS_MS = [1_000, 2_000, 4_000, 8_000]

// How often an open INVI tab swaps its login for a fresh one. Each renewal pushes
// expiry a week out, never past a month from the original login (backend/auth.py
// WEB_SESSION_IDLE_DAYS / WEB_SESSION_MAX_DAYS), so anyone using INVI at least
// weekly only sees the login page once a month.
const SESSION_RENEW_INTERVAL_MS = 60 * 60 * 1000

// What the login page says for each way a login can fail. Only INVI rejecting
// the credentials blames the password: on 2026-09-30 a request lost on the
// office network read "Invalid username or password" and looked like a typo.
const LOGIN_REJECTED_MESSAGE = "Invalid username or password"
const LOGIN_ACCOUNT_OFF_MESSAGE =
  "This account has been turned off. Ask your INVI administrator to turn it back on."
const LOGIN_UNREACHABLE_MESSAGE =
  "Can't reach INVI right now. Check your internet connection and try again."

// INVI answered and said this login is no longer valid (expired, or the account
// was turned off). The only failure that should log someone out.
class SessionRejectedError extends Error {}

const wait = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms))

// Another tab may have logged in while this check was in flight; only remove
// the token the check was actually about, never a newer one.
const clearTokenIfUnchanged = (checkedToken: string): void => {
  if (localStorage.getItem(TOKEN_KEY) === checkedToken) {
    localStorage.removeItem(TOKEN_KEY)
  }
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined)

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null)
  const [token, setToken] = useState<string | null>(() =>
    localStorage.getItem(TOKEN_KEY)
  )
  const [isLoading, setIsLoading] = useState<boolean>(true)

  const fetchMe = useCallback(async (authToken: string): Promise<User> => {
    const resp = await fetch(`${API.baseUrl}${API.endpoints.me}`, {
      headers: { Authorization: `Bearer ${authToken}` },
    })
    if (resp.status === 401 || resp.status === 403) {
      throw new SessionRejectedError("Session expired")
    }
    if (!resp.ok) {
      throw new Error(`Identity check failed: HTTP ${resp.status}`)
    }
    return resp.json()
  }, [])

  // Restore session on mount. Only an explicit "not logged in" answer from INVI
  // clears the saved token. A check that fails to arrive, or a server error, is
  // retried; if INVI stays unreachable the token is kept so a later reload works.
  useEffect(() => {
    const saved = localStorage.getItem(TOKEN_KEY)
    if (!saved) {
      setIsLoading(false)
      return
    }
    let cancelled = false
    const restoreSession = async () => {
      for (let attempt = 0; ; attempt++) {
        try {
          const u = await fetchMe(saved)
          if (cancelled) return
          setUser(u)
          setToken(saved)
          break
        } catch (err) {
          if (cancelled) return
          if (err instanceof SessionRejectedError) {
            clearTokenIfUnchanged(saved)
            setUser(null)
            setToken(null)
            break
          }
          if (attempt >= IDENTITY_CHECK_RETRY_DELAYS_MS.length) break
          await wait(IDENTITY_CHECK_RETRY_DELAYS_MS[attempt])
          if (cancelled) return
        }
      }
      setIsLoading(false)
    }
    restoreSession()
    return () => {
      cancelled = true
    }
  }, [fetchMe])

  const login = useCallback(
    async (username: string, password: string): Promise<User> => {
      const body = new URLSearchParams()
      body.append("username", username)
      body.append("password", password)

      let resp: Response
      try {
        resp = await fetch(`${API.baseUrl}${API.endpoints.login}`, {
          method: "POST",
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          body: body.toString(),
        })
      } catch {
        throw new Error(LOGIN_UNREACHABLE_MESSAGE)
      }
      if (resp.status === 403) throw new Error(LOGIN_ACCOUNT_OFF_MESSAGE)
      if (resp.status >= 400 && resp.status < 500) throw new Error(LOGIN_REJECTED_MESSAGE)
      if (!resp.ok) throw new Error(LOGIN_UNREACHABLE_MESSAGE)

      const data = (await resp.json()) as { access_token: string }
      localStorage.setItem(TOKEN_KEY, data.access_token)
      setToken(data.access_token)
      try {
        const profile = await fetchMe(data.access_token)
        setUser(profile)
        return profile
      } catch {
        // The password was right but the follow-up "who am I?" was lost. Undo
        // the half-finished login so pressing Log in again starts clean.
        clearTokenIfUnchanged(data.access_token)
        setToken(null)
        throw new Error(LOGIN_UNREACHABLE_MESSAGE)
      }
    },
    [fetchMe]
  )

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY)
    setUser(null)
    setToken(null)
  }, [])

  const adoptToken = useCallback((newToken: string) => {
    localStorage.setItem(TOKEN_KEY, newToken)
    setToken(newToken)
  }, [])

  // Swap the saved login for a fresh one. INVI refusing it (expired, or the
  // account turned off) logs out, and ProtectedRoute then shows the login page.
  // A lost request changes nothing; the next renewal tries again.
  const renewSession = useCallback(async () => {
    const presented = localStorage.getItem(TOKEN_KEY)
    if (!presented) return
    // A logout, or a login in another tab, while this was in flight wins.
    const stillCurrent = () => localStorage.getItem(TOKEN_KEY) === presented
    try {
      const resp = await fetch(`${API.baseUrl}${API.endpoints.renewSession}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${presented}` },
      })
      if (resp.status === 401 || resp.status === 403) {
        if (stillCurrent()) {
          localStorage.removeItem(TOKEN_KEY)
          setUser(null)
          setToken(null)
        }
        return
      }
      if (!resp.ok) return
      const data = (await resp.json()) as { access_token: string }
      if (!stillCurrent()) return
      localStorage.setItem(TOKEN_KEY, data.access_token)
      setToken(data.access_token)
    } catch {
      // Request lost on the way; keep the current login.
    }
  }, [])

  // Keep the login fresh while INVI is open: renew as soon as someone is signed
  // in (so opening INVI after days away earns a new week), then every hour.
  const isSignedIn = user !== null
  useEffect(() => {
    if (!isSignedIn) return
    renewSession()
    const timer = setInterval(renewSession, SESSION_RENEW_INTERVAL_MS)
    return () => clearInterval(timer)
  }, [isSignedIn, renewSession])

  const hasPermission = useCallback(
    (permission: string) => {
      if (!user) return false
      // Union across the legacy primary role + all multi-roles.
      const allRoles = [...(user.role ? [user.role] : []), ...(user.roles ?? [])]
      return allRoles.some((r) => r.permissions.some((p) => p.name === permission))
    },
    [user]
  )

  const refreshUser = useCallback(async () => {
    const saved = localStorage.getItem(TOKEN_KEY)
    if (!saved) return
    try {
      const u = await fetchMe(saved)
      setUser(u)
    } catch {
      /* session expired — handled by next navigation */
    }
  }, [fetchMe])

  const value = useMemo<AuthContextValue>(
    () => ({ user, token, isLoading, login, logout, adoptToken, hasPermission, refreshUser }),
    [user, token, isLoading, login, logout, adoptToken, hasPermission, refreshUser]
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export const useAuth = (): AuthContextValue => {
  const ctx = useContext(AuthContext)
  if (!ctx) {
    throw new Error("useAuth must be used within AuthProvider")
  }
  return ctx
}
